import { useSession } from "@/lib/auth-client";
import { setSuperAdminView, useIsSuperAdmin, useSuperAdminView } from "@/lib/use-superadmin-view";
import { cn } from "@/lib/utils";
import { useParams } from "@tanstack/react-router";
import { Building2, Eye, EyeOff, Server, Shield, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

// Floating bottom-right speed dial, rendered for super admins only. The toggle
// reveals super-admin extras on customer pages (off by default, per tab — see
// use-superadmin-view); the links open the admin console in a new tab. The
// button turns indigo with a green dot while the extras are shown.
export function SuperAdminDock() {
  const isSuperAdmin = useIsSuperAdmin();
  const enabled = useSuperAdminView();
  const { data: session } = useSession();
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const orgId = params.orgId ?? session?.session.activeOrganizationId ?? undefined;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!isSuperAdmin) return null;

  // Top to bottom; the bottom item sits next to the FAB and animates first.
  const items: DialItemProps[] = [
    ...(orgId ? [{ label: "Open org in admin", icon: <Building2 />, href: `/superadmin/organizations/${orgId}` }] : []),
    ...(params.containerId
      ? [{ label: "Open infra", icon: <Server />, href: `/superadmin/infrastructure/${params.containerId}` }]
      : []),
    {
      label: enabled ? "Hide superadmin data" : "Show superadmin data",
      icon: enabled ? <Eye /> : <EyeOff />,
      active: enabled,
      onClick: () => setSuperAdminView(!enabled),
    },
  ];

  return (
    <div ref={ref} className="pointer-events-none fixed right-4 bottom-4 z-40 flex flex-col items-end gap-2.5">
      {/* Always mounted so closing animates too; inert keeps hidden actions out
          of the tab order. Delays stagger from the FAB upward on open only.
          The wrapper lets clicks through, since the hidden items still give it
          size; the button and the open list opt back in. */}
      <div
        aria-hidden={!open}
        inert={!open || undefined}
        className={cn("flex flex-col items-end gap-2.5", open ? "pointer-events-auto" : "pointer-events-none")}
      >
        {items.map((item, i) => (
          <DialItem key={item.href ?? "toggle"} {...item} open={open} delayMs={(items.length - 1 - i) * 30} />
        ))}
      </div>
      <button
        type="button"
        aria-label="Superadmin tools"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "pointer-events-auto relative grid size-12 place-items-center rounded-full border shadow-lg transition-[background-color,color,border-color,translate] duration-200 hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 motion-reduce:transition-none [&_svg]:absolute [&_svg]:size-5 [&_svg]:transition-[opacity,rotate] [&_svg]:duration-200 motion-reduce:[&_svg]:transition-none",
          enabled
            ? "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 dark:border-indigo-400 dark:bg-indigo-400 dark:text-indigo-950 dark:hover:bg-indigo-300"
            : "bg-background text-muted-foreground hover:text-foreground",
        )}
      >
        <Shield className={open ? "rotate-90 opacity-0" : ""} />
        <X className={open ? "" : "-rotate-90 opacity-0"} />
        {enabled && (
          <span className="absolute top-0.5 right-0.5 size-2.5 rounded-full border-2 border-background bg-green-500" />
        )}
      </button>
    </div>
  );
}

type DialItemProps = {
  label: string;
  icon: ReactNode;
  href?: string;
  active?: boolean;
  onClick?: () => void;
};

function DialItem({
  label,
  icon,
  href,
  active,
  onClick,
  open,
  delayMs,
}: DialItemProps & { open: boolean; delayMs: number }) {
  const circle = cn(
    "grid size-10 place-items-center rounded-full border shadow-md transition-colors [&_svg]:size-4",
    active
      ? "border-indigo-600 bg-indigo-600 text-white dark:border-indigo-400 dark:bg-indigo-400 dark:text-indigo-950"
      : "bg-background text-foreground group-hover:bg-muted",
  );
  const content = (
    <>
      <span className="rounded-md bg-foreground px-2 py-1 text-xs font-medium whitespace-nowrap text-background shadow-md">
        {label}
      </span>
      <span className={circle}>{icon}</span>
    </>
  );
  const cls = cn(
    "group mr-1 flex items-center gap-2.5 rounded-full transition-[opacity,translate,scale] duration-200 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 motion-reduce:transition-none",
    open ? "translate-y-0 scale-100 opacity-100" : "pointer-events-none translate-y-2.5 scale-95 opacity-0",
  );
  const style = { transitionDelay: open ? `${delayMs}ms` : "0ms" };
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style}>
      {content}
    </a>
  ) : (
    <button type="button" aria-pressed={active} onClick={onClick} className={cls} style={style}>
      {content}
    </button>
  );
}
